import axios from 'axios';
import { getSystemPrompt } from 'cc-editor/config/aiCopilotConfig';

export const GENERATE_AI_RESPONSE = 'GENERATE_AI_RESPONSE';
export const CLEAR_AI_SUGGESTION = 'CLEAR_AI_SUGGESTION';

/**
 * Resets the current AI suggestion in the state
 */
export function clearAiSuggestion() {
  return {
    type: CLEAR_AI_SUGGESTION,
  };
}

/**
 * Generates an AI response via Symfony Proxy (/ai_assistant.json)
 *
 * @param {Object} params
 * @param {string} params.instruction User prompt or quick action instruction
 * @param {string} [params.contextContent=''] HTML or text context from selected block
 * @param {string} params.locale Current UI locale (e.g., 'de', 'en')
 * @param {string} [params.defaultUserPrompt=''] Global user directives/prompt from settings
 * @param {string} [params.model='gpt-4o'] OpenAI model default gpt-4o, others: gpt-4o-mini
 * @param {number} [params.temperature=0.7] Controls randomness/creativity (0.0 to 2.0)
 */
export function generateAiResponse({
  instruction,
  contextContent = '',
  locale,
  defaultUserPrompt = '',
  model = 'gpt-4o',
  temperature = 0.7,
}) {
  let systemPrompt = getSystemPrompt(locale);

  if (defaultUserPrompt.trim()) {
    systemPrompt += `\n\nGLOBAL USER DIRECTIVES (ALWAYS FOLLOW):\n${defaultUserPrompt.trim()}`;
  }

  // Combine block content and user instruction if context exists
  const userMessageContent = contextContent.trim()
    ? `REFERENCE HTML CONTENT TO PRESERVE/MODIFY:\n${contextContent}\n\nUSER INSTRUCTION:\n${instruction}`
    : instruction;

  const body = {
    model,
    messages: [
      {
        role: 'system',
        content: systemPrompt,
      },
      {
        role: 'user',
        content: userMessageContent,
      },
    ],
    temperature: temperature,
  };

  // Cleans up any Markdown code blocks (```html ... ```) if GPT includes them
  return {
    type: GENERATE_AI_RESPONSE,
    payload: axios.post('/api/ai_assistant.json', body).then(({ data }) => {
      let content = data?.choices?.[0]?.message?.content;

      if (!content) {
        throw new Error('No valid response received from the AI.');
      }

      // 1. Remove Markdown code blocks (```html ... ```)
      content = content
        .replace(/^```(?:html)?\n?/i, '')
        .replace(/\n?```$/i, '')
        .trim();

      // 2. If full-text HTML (<!DOCTYPE... <body>...</body>) was provided after all: Extract only the <body> content.
      if (content.includes('<body') && content.includes('</body>')) {
        const bodyMatches = content.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        if (bodyMatches && bodyMatches[1]) {
          content = bodyMatches[1].trim();
        }
      }

      // 3. Clean up remaining DOCTYPE, html, and head tags as a precaution
      content = content
        .replace(/<!DOCTYPE[^>]*>/gi, '')
        .replace(/<\/?(html|head|meta|title)[^>]*>/gi, '')
        .trim();

      return content;
    }),
  };
}
